import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export function MentalControlOverthinkingHub() {
  useEffect(() => {
    document.title = "Mental Control for High-Functioning Minds";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 
        "Structured thinking systems to stop overthinking, break mental loops, and set cognitive boundaries. Built for analytical minds."
      );
    }
  }, []);

  const posts = [
    {
      title: "Why Smart People Can't Stop Overthinking (And What Actually Helps)",
      excerpt: "High cognitive capacity without boundaries creates mental loops. Structured frameworks redirect that energy into productive output.",
      image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=600&fit=crop",
      slug: "/why-smart-people-overthink"
    },
    {
      title: "The Difference Between Rumination and Reflection (And Why It Matters)",
      excerpt: "Reflection moves forward. Rumination repeats. Learn to identify which pattern you're in and how to interrupt unproductive cycles.",
      image: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&h=600&fit=crop",
      slug: "/rumination-vs-reflection"
    },
    {
      title: "Decision Fatigue Is Killing Your Productivity (Here's How to Fix It)",
      excerpt: "Too many decisions without clear boundaries drain mental energy. Decision frameworks preserve cognitive resources for what matters.",
      image: "https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=800&h=600&fit=crop",
      slug: "/decision-fatigue"
    },
    {
      title: "Why Meditation Doesn't Work for Analytical Thinkers (And What Does)",
      excerpt: "Analytical thinkers need structure, not emptiness. Mental control systems work with your thinking style, not against it.",
      image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&h=600&fit=crop",
      slug: "/meditation-analytical-thinkers"
    },
    {
      title: "How to Stop Replaying Conversations in Your Head (It's Simpler Than You Think)",
      excerpt: "Conversational rumination is pattern recognition without new data. Boundary techniques interrupt the replay before it loops.",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&h=600&fit=crop",
      slug: "/stop-replaying-conversations"
    },
    {
      title: "Mental Boundaries: What They Are and Why You Need Them",
      excerpt: "Cognitive boundaries define when thinking starts and stops. Without them, mental processing continues indefinitely.",
      image: "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&h=600&fit=crop",
      slug: "/mental-boundaries"
    },
    {
      title: "AI Anxiety and the Overthinking Loop",
      excerpt: "Future uncertainty triggers analysis without endpoints. Structured forecasting techniques contain projection without suppressing it.",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=600&fit=crop",
      slug: "/ai-anxiety"
    },
    {
      title: "Why Your Brain Won't Shut Off at Night",
      excerpt: "Nighttime thinking isn't insomnia—it's unprocessed cognitive load. Evening protocols discharge mental activity before sleep.",
      image: "https://images.unsplash.com/photo-1511439922378-c7a99d5b8f6d?w=800&h=600&fit=crop",
      slug: "/brain-at-night"
    },
    {
      title: "The Hidden Cost of Mental Loops at Work",
      excerpt: "Repeated analysis reduces output quality and decision speed. Loop interruption techniques preserve performance under pressure.",
      image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop",
      slug: "/mental-loops-at-work"
    },
    {
      title: "Mental Containment System: How to Think Without Getting Stuck",
      excerpt: "Productive thinking has clear start and stop points. Mental containment systems let you think deeply without looping.",
      image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=600&fit=crop",
      slug: "/mental-containment-system"
    },
    {
      title: "Clarity vs Calm: A Cognitive Perspective",
      excerpt: "Calm is not the goal. Clarity is. Control produces clarity. Calm follows as a byproduct, not a method.",
      image: "https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=800&h=600&fit=crop",
      slug: "/clarity-vs-calm"
    },
    {
      title: "Overthinking Under Pressure: Decision Frameworks That Work When Stakes Are High",
      excerpt: "High-stakes situations trigger accelerated analysis. Pressure-tested frameworks maintain structure when mental load increases.",
      image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&h=600&fit=crop",
      slug: "/overthinking-under-pressure"
    }
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 py-6 px-6">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="text-[#2C5F7D] hover:text-[#234a5f] transition-colors">
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
            Mental Control for High-Functioning Minds
          </h1>

          <div className="prose prose-lg max-w-none space-y-6 text-gray-700 leading-relaxed">
            <p>
              Mental control is the structured management of cognitive activity. It is not about eliminating thought—it is about directing mental energy toward productive outcomes instead of allowing it to cycle through unproductive loops. <strong>Overthinking is not a personal failure. It is excess mental capacity without boundaries.</strong>
            </p>

            <p>
              High-functioning adults experience overthinking because their cognitive systems are built for problem-solving. When modern problems lack clear endpoints, thinking continues indefinitely. You replay conversations. You re-evaluate completed decisions. You analyze scenarios you cannot act on. This is not dysfunction. It is mental activity without containment.
            </p>

            <p>
              Structured thinking systems address the root cause: the absence of cognitive boundaries. Rumination repeats without progress. Reflection extracts insight and stops. The difference is structure. Decision fatigue occurs when too many choices drain mental resources without recovery protocols. Racing thoughts at night indicate unprocessed cognitive load, not insomnia.
            </p>

            <p>
              <strong>Mental control frameworks are designed for analytical minds that think in systems, not for those seeking emotional reassurance.</strong> These methods do not suppress thinking—they redirect it. Cognitive boundaries define when analysis starts and when it stops. Without these limits, mental processing has no natural endpoint.
            </p>
          </div>
        </div>
      </section>

      {/* Content Hub Explanation */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            What You'll Learn in This Section
          </h2>
          
          <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
            <p>
              This content hub focuses on structured cognitive frameworks for high-performing adults who experience persistent mental activity. You will learn the functional difference between rumination and reflection, why mental loops repeat without resolution, and why traditional calming techniques fail analytical minds.
            </p>
            <p>
              These articles explain why structure works better than suppression. Mental control is not about quieting your mind—it is about defining clear boundaries so your thinking has direction, not just duration.
            </p>
          </div>
        </div>
      </section>

      {/* Grid Layout - 3 Posts Per Row */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              <article key={index} className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                    {post.title}
                  </h3>
                  <p className="text-base text-gray-600 mb-4 leading-relaxed">
                    {post.excerpt}
                  </p>
                  <Link 
                    to={post.slug}
                    className="inline-block text-[#2C5F7D] font-medium hover:text-[#234a5f] transition-colors">
                    Read More →
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Authority Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Structured Thinking vs Emotional Coping
          </h2>
          
          <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
            <p>
              This category focuses on structured cognitive frameworks for mental control. These are not therapy techniques. They are not motivational strategies. They are not calming exercises.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-6 space-y-3">
              <p className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[#2C5F7D] rounded-full mt-2 flex-shrink-0"></span>
                <span>These frameworks are designed for analytical thinkers who need structure, not reassurance.</span>
              </p>
              <p className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[#2C5F7D] rounded-full mt-2 flex-shrink-0"></span>
                <span>They work with your cognitive style, not against it.</span>
              </p>
              <p className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[#2C5F7D] rounded-full mt-2 flex-shrink-0"></span>
                <span>They prioritize control over calm, because control produces clarity.</span>
              </p>
              <p className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[#2C5F7D] rounded-full mt-2 flex-shrink-0"></span>
                <span>They are repeatable systems, not one-time interventions.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-8" itemScope itemType="https://schema.org/FAQPage">
            <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="text-xl font-bold text-gray-900 mb-3" itemProp="name">
                What is overthinking?
              </h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-base text-gray-700 leading-relaxed" itemProp="text">
                  Overthinking is mental processing that continues beyond the point of productive analysis. It occurs when the mind replays scenarios, re-evaluates completed decisions, or analyzes problems without new information. Normal thinking moves toward resolution. Overthinking repeats without progress.
                </p>
              </div>
            </div>

            <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="text-xl font-bold text-gray-900 mb-3" itemProp="name">
                What is rumination?
              </h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-base text-gray-700 leading-relaxed" itemProp="text">
                  Rumination is repetitive, circular thinking about past events or decisions without forward movement. Unlike reflection, which extracts insight and closes, rumination loops without resolution. It is mental activity that consumes energy but produces no usable output.
                </p>
              </div>
            </div>

            <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="text-xl font-bold text-gray-900 mb-3" itemProp="name">
                How do you stop mental loops?
              </h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-base text-gray-700 leading-relaxed" itemProp="text">
                  Mental loops stop when you implement cognitive boundaries—defined limits that determine when mental processing should begin and when it should stop. Structured frameworks like the 3×3×3 Method interrupt loops by redirecting mental energy before it cycles. The goal is not suppression. It is containment.
                </p>
              </div>
            </div>

            <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="text-xl font-bold text-gray-900 mb-3" itemProp="name">
                Is overthinking a mental disorder?
              </h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-base text-gray-700 leading-relaxed" itemProp="text">
                  Overthinking itself is not a mental disorder. It is a cognitive pattern that occurs when mental processing lacks clear endpoints. While it can co-occur with anxiety or depression, overthinking is often a function of high cognitive capacity without structure. If overthinking interferes with daily functioning, professional evaluation is recommended.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Linking Strategy */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Start Here
          </h2>
          
          <div className="space-y-4">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              If you're new to mental control frameworks, begin with these foundational resources:
            </p>
            
            <div className="space-y-4 bg-gray-50 rounded-lg p-6">
              <Link 
                to="/blog/structured-mental-control"
                className="block text-lg font-medium text-[#2C5F7D] hover:text-[#234a5f] transition-colors">
                → Structured mental control frameworks for high-functioning adults
              </Link>
              <Link 
                to="/blog/stopping-mental-loops"
                className="block text-lg font-medium text-[#2C5F7D] hover:text-[#234a5f] transition-colors">
                → Stopping mental loops before they drain your energy
              </Link>
              <Link 
                to="/blog/cognitive-boundaries-framework"
                className="block text-lg font-medium text-[#2C5F7D] hover:text-[#234a5f] transition-colors">
                → The cognitive boundaries framework: why structure works
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto text-center text-gray-600">
          <p className="mb-2">The Overthinking Reset</p>
          <p className="text-sm">© 2024. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
